import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { Input, type InputProps } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Compact shadcn-style input group adapted to Mora's form geometry.
 *
 * The group owns the only border and focus treatment. Nested controls remove
 * their own chrome so addons and text read as one field.
 */
const inputGroupVariants = cva(
  cn(
    'group/input-group flex h-11 w-full items-center rounded-[var(--radius)]',
    'border border-input bg-background outline-none',
    'transition-colors duration-150 ease-out md:h-9',
    'has-[[data-slot=input-group-control]:focus-visible]:border-foreground/30',
    'data-[invalid=true]:border-destructive/60',
    'data-[disabled=true]:cursor-wait data-[disabled=true]:opacity-60',
  ),
);

function InputGroup({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(inputGroupVariants(), className)}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva('flex h-full shrink-0 items-center justify-center p-1', {
  variants: {
    align: {
      'inline-start': 'order-first',
      'inline-end': 'order-last',
    },
  },
  defaultVariants: {
    align: 'inline-start',
  },
});

interface InputGroupAddonProps
  extends React.ComponentProps<'div'>, VariantProps<typeof inputGroupAddonVariants> {}

function InputGroupAddon({
  className,
  align,
  onClick,
  ...props
}: InputGroupAddonProps): React.JSX.Element {
  return (
    <div
      data-slot="input-group-addon"
      data-align={align ?? 'inline-start'}
      role="group"
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || (event.target as HTMLElement).closest('button')) return;
        event.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
}

/**
 * A plain-text addon (currency symbol, unit, protocol) that reads at the same
 * scale as the input's own text — `text-base md:text-sm`, matching `Input`'s
 * own `size="sm"` typography, so a "$" or "USD" sits inline with what the user
 * typed instead of reading as a smaller, separate badge.
 */
function InputGroupText({ className, ...props }: React.ComponentProps<'span'>): React.JSX.Element {
  return (
    <span
      data-slot="input-group-text"
      className={cn('text-base text-muted-foreground md:text-sm', className)}
      {...props}
    />
  );
}

const InputGroupInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <Input
      ref={ref}
      data-slot="input-group-control"
      size="sm"
      className={cn(
        // `dark:bg-transparent` pairs the override — the GROUP paints the
        // surface here, so the control must stay transparent in both themes.
        // A bare `bg-transparent` and `Input`'s own `dark:bg-input` are
        // different tailwind-merge groups and would otherwise fight over
        // dark-mode background-color by stylesheet source order.
        'h-full flex-1 rounded-none border-0 bg-transparent dark:bg-transparent pl-0 pr-3 shadow-none',
        'placeholder:text-muted-foreground/70',
        'focus-visible:border-0 focus-visible:ring-0 focus-visible:shadow-none',
        className,
      )}
      {...props}
    />
  ),
);
InputGroupInput.displayName = 'InputGroupInput';

/**
 * The action that lives INSIDE the field, so an input and its verb read as one
 * control instead of a box sitting next to a button.
 *
 * Two deliberate departures from the upstream shadcn component:
 *
 * 1. **`variant` defaults to `default`, not `ghost`.** `ghost` is banned in this
 *    repo; `default` is the dark neutral fill that `button.tsx` documents as the
 *    app's real primary CTA (blue is licensed only for send / focus /
 *    active-toggle). Pass `variant="flat"` for a quiet icon-only action.
 * 2. **Every size repeats its `md:` prefix.** `button.tsx` warns that a bare
 *    `h-7` override silently loses to a size variant's own `md:h-9` at desktop
 *    widths, so the heights here are written responsively. `h-9 md:h-7` inside
 *    the group's `h-11 md:h-9` leaves the ~2px inset that makes the button read
 *    as nested rather than stacked.
 */
const inputGroupButtonVariants = cva('shrink-0 shadow-none', {
  variants: {
    size: {
      sm: 'h-9 md:h-7 rounded-md px-2.5 text-caption gap-1.5',
      'icon-sm': 'size-9 md:size-7 rounded-md p-0',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

interface InputGroupButtonProps
  extends Omit<ButtonProps, 'size'>, VariantProps<typeof inputGroupButtonVariants> {}

function InputGroupButton({
  className,
  type = 'button',
  variant = 'default',
  size,
  ...props
}: InputGroupButtonProps): React.JSX.Element {
  return (
    <Button
      type={type}
      variant={variant}
      data-slot="input-group-button"
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
}

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText };
